import DashboardLayout from "components/layouts/dashboard-layout";
import PageLinks from "components/page-links";
import React, { useEffect, useMemo, useState } from "react";
import {
  GrayBody,
  GrayCard,
  GrayHeader,
  TableContainer,
  TableItem
} from "../../components/style";
import Typography from "components/typography";
import { Colors } from "utils/colors";
import PrimaryButton from "components/buttons/primary-button";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import LinkText from "components/typography/linkText";
import Modal from "components/modal";
import ModalTemplate from "components/modal/modal-template";
import { IUser } from "types/user";
import api from "services/axiosInstance";
import Toast from "components/toast";

const SettingsSubscriptions = () => {
  const navigate = useNavigate();
  const [cancelledSub, setCancelledSub] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [isModalActive, setIsModalActive] = useState(false);
  const [subscription, setSubscription] = useState<string>("pro");

  const hasSub = useMemo(() => {
    if (subscription === "free" || subscription === "") {
      return false;
    }
    return true;
  }, [subscription]);

  const CancelSubscription = async () => {
    try {
      const { data } = await api.post("/users/upgrade-plan", {
        newPlan: "free"
      });
      setCancelling(true);
      Toast({ type: "success", message: data.message });
      await PlanStatus();
      setCancelledSub(true);
    } catch (error) {
      Toast({ error });
    } finally {
      setCancelling(false);
    }
  };

  const PlanStatus = async () => {
    try {
      const { data } = await api.get("/users/plan-status");
      setSubscription(data.subscription);
    } catch (error) {
      Toast({ error });
    }
  };

  useEffect(() => {
    PlanStatus();
  }, []);

  return (
    <DashboardLayout>
      <>
        <PageLinks
          from={{ name: "Settings", link: "/settings" }}
          current="Manage Subscription"
        />
        <GrayCard>
          <GrayHeader>
            <Typography size="2xl" weight="medium">
              Billings & Subscription
            </Typography>

            <PrimaryButton
              onClick={() => {
                navigate(-1);
              }}
              text="CANCEL"
              fitContent
              variant="border"
              textColor={Colors.white100}
            />
          </GrayHeader>
          <GrayBody>
            <TableContainer>
              <TableItem>
                <Typography size="xl" color={Colors.grey100} weight="semibold">
                  Your Plan
                </Typography>

                {subscription === "free" ? (
                  <LinkText to="/pricing" text="Select A Plan" />
                ) : (
                  <Typography size="lg" color={Colors.grey500}>
                    {subscription}
                  </Typography>
                )}
              </TableItem>
              <TableItem>
                <Typography size="xl" color={Colors.grey100} weight="semibold">
                  Status
                </Typography>
                <Typography size="lg" color={Colors.grey500}>
                  Active
                </Typography>
              </TableItem>
              <TableItem>
                <Typography size="xl" color={Colors.grey100} weight="semibold">
                  Next Renewal
                </Typography>
                <Typography size="lg" color={Colors.grey500}>
                  12-11-2025
                </Typography>
              </TableItem>
              <TableItem>
                <Typography size="xl" color={Colors.grey100} weight="semibold">
                  Payment Amount
                </Typography>
                <Typography size="lg" color={Colors.grey500}>
                  $4/month
                </Typography>
              </TableItem>
            </TableContainer>
            <Buttons>
              <PrimaryButton
                onClick={() => {
                  if (hasSub) {
                    setIsModalActive(true);
                  }
                }}
                disabled={!hasSub}
                text="Cancel Subscription"
                variant="d-fade"
              />
              <PrimaryButton
                onClick={() => {
                  navigate("/pricing");
                }}
                text="Upgrade Plan"
              />
            </Buttons>
          </GrayBody>
        </GrayCard>
        <Modal
          isActive={isModalActive}
          closeModal={() => setIsModalActive(false)}
        >
          {!cancelledSub ? (
            <ModalTemplate
              header="Cancel Subscription?"
              close={() => setIsModalActive(false)}
              twoButtons
              confirmText="Yes"
              cancelText="No"
              confirm={CancelSubscription}
              loading={cancelling}
            >
              <Typography size="lg" weight="medium" color={Colors.grey400}>
                Your plan will not be renewed after it has expired, would you
                like to still cancel?
              </Typography>
            </ModalTemplate>
          ) : (
            <ModalTemplate close={() => setIsModalActive(false)} footer={false}>
              <CanceledModalBody>
                <Typography size="4xl" weight="semibold" align="center">
                  Your Subscription has been Cancelled
                </Typography>
                <CancelIcon>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="100%"
                    height="100%"
                    viewBox="0 0 80 80"
                    fill="none"
                  >
                    <path
                      d="M58.3572 45.6132C57.3787 42.4396 56.1555 39.3652 54.6944 36.3834C54.0266 35.0016 53.2993 33.6462 52.5258 32.3041C52.0233 31.438 51.501 30.5851 50.9588 29.7454C48.8696 26.4859 46.5026 23.4446 43.8646 20.6148C40.5456 17.0578 37.0018 14.1884 33.2266 12.0066C29.4514 9.83135 25.9076 8.6016 22.5886 8.33053C22.4696 8.3173 22.3506 8.31069 22.2315 8.30408C19.4547 8.12557 16.982 8.5421 14.8266 9.55367C14.5225 9.69912 14.2183 9.8578 13.9274 10.0231C11.4745 11.4181 9.53076 13.6132 8.09605 16.595C6.66795 19.5768 5.94727 23.233 5.94727 27.557C5.94727 31.8809 6.66795 36.3702 8.09605 41.0049C9.53076 45.6396 11.4745 50.076 13.9274 54.314C16.3803 58.5454 19.2696 62.4462 22.5886 66.0033C25.9076 69.5603 29.4514 72.4297 33.2266 74.6115C37.0018 76.7867 40.5456 78.0165 43.8646 78.2876C46.8332 78.5322 49.4646 78.1024 51.739 77.0115C52.0101 76.8793 52.2679 76.7404 52.5258 76.595C54.4894 75.471 56.1291 73.8446 57.4448 71.7024C57.7687 71.1735 58.0729 70.6115 58.3572 70.0231C59.7853 67.0413 60.5059 63.3851 60.5059 59.0545C60.5059 54.7239 59.7853 50.2479 58.3572 45.6132ZM37.0811 45.5669L37.1671 45.719L44.9555 59.1603C45.458 60.0198 45.7026 60.8925 45.7026 61.7851C45.7026 62.6776 45.458 63.2528 44.9555 63.5371C44.453 63.828 43.8183 63.7421 43.0448 63.2991C42.2712 62.8495 41.6365 62.195 41.134 61.3355L33.2266 47.6892L28.7175 50.2611L25.3125 52.1983C24.8167 52.4892 24.182 52.4033 23.4084 51.9603C22.6349 51.5107 22.0001 50.8561 21.4977 49.9966C20.9952 49.1305 20.7439 48.2578 20.7439 47.3719C20.7439 46.4859 20.9952 45.8975 21.4977 45.6132L24.982 43.6231L29.4051 41.1041L21.4977 27.4578C20.9952 26.5983 20.7439 25.7256 20.7439 24.833C20.7439 23.9404 20.9952 23.3652 21.4977 23.0743C22.0001 22.79 22.6349 22.876 23.4084 23.319C24.182 23.7685 24.8167 24.4231 25.3125 25.2826L33.2266 38.9289L41.134 34.4198C41.4844 34.2148 41.8944 34.195 42.377 34.3537C42.4497 34.3735 42.5225 34.4 42.5952 34.4396C42.7406 34.4925 42.8861 34.5652 43.0448 34.6578C43.8183 35.1074 44.453 35.7619 44.9555 36.6214C45.458 37.4809 45.7026 38.3603 45.7026 39.2462C45.7026 40.1322 45.458 40.7206 44.9555 41.0049L37.0481 45.514L37.0811 45.5669Z"
                      fill="#FFBABA"
                      stroke="#FF3333"
                      stroke-width="1.32231"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M42.6279 34.4926L29.4048 41.1041L21.4974 27.4579C20.9949 26.5984 20.7437 25.7256 20.7437 24.8331C20.7437 23.9405 20.9949 23.3653 21.4974 23.0744C21.9999 22.7901 22.6346 22.876 23.4081 23.319C24.1817 23.7686 24.8164 24.4232 25.3122 25.2827L33.2263 38.9289L42.3767 34.3537C42.4494 34.3736 42.5222 34.4 42.5949 34.4397L42.6279 34.4926Z"
                      fill="#FFBABA"
                      stroke="#FF3333"
                      stroke-width="1.32231"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M45.7026 61.7851C45.7026 62.6711 45.458 63.2529 44.9556 63.5372C44.4531 63.8281 43.8184 63.7421 43.0448 63.2992C42.2713 62.8496 41.6365 62.195 41.134 61.3355L33.2266 47.6892L37.1671 45.719L44.9556 59.1603C45.458 60.0198 45.7026 60.8925 45.7026 61.7851Z"
                      fill="#FFBABA"
                      stroke="#FF3333"
                      stroke-width="1.32231"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M37.0808 45.5671L37.1668 45.7191L33.2263 47.6894L28.7172 50.2613L25.3122 52.1985C24.8164 52.4894 24.1817 52.4034 23.4081 51.9604C22.6346 51.5109 21.9999 50.8563 21.4974 49.9968C20.9949 49.1307 20.7437 48.258 20.7437 47.372C20.7437 46.4861 20.9949 45.8976 21.4974 45.6133L24.9817 43.6233L29.4048 41.1042L42.6279 34.4927L34.7205 39.0018C34.218 39.2861 33.9668 39.8745 33.9668 40.7604C33.9668 41.6464 34.218 42.5191 34.7205 43.3852C35.223 44.2447 35.8577 44.8993 36.6313 45.3489C36.7899 45.4414 36.9354 45.5142 37.0808 45.5671Z"
                      fill="#FFBABA"
                      stroke="#FF3333"
                      stroke-width="1.32231"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M73.7291 52.4429C73.7291 56.7735 73.0085 60.4297 71.5804 63.4115C70.1457 66.3933 68.2019 68.5818 65.749 69.9834C65.5969 70.0694 65.4382 70.1553 65.2795 70.2413L65.1606 70.3008L51.7391 77.0115C52.0101 76.8793 52.268 76.7404 52.5258 76.595C54.4895 75.471 56.1291 73.8446 57.4448 71.7024C57.7688 71.1735 58.0729 70.6115 58.3572 70.0231C59.7853 67.0413 60.506 63.3851 60.506 59.0545C60.506 54.7239 59.7853 50.2479 58.3572 45.6132C57.3787 42.4396 56.1556 39.3652 54.6944 36.3834C54.0267 35.0016 53.2994 33.6462 52.5258 32.3041C52.0234 31.438 51.501 30.5851 50.9589 29.7454C48.8696 26.4859 46.5027 23.4446 43.8647 20.6148C40.5457 17.0578 37.0019 14.1884 33.2267 12.0066C29.4515 9.83136 25.9077 8.6016 22.5886 8.33053C22.4696 8.31731 22.3506 8.31069 22.2316 8.30408C19.4548 8.12557 16.982 8.5421 14.8267 9.55367L27.1506 3.41152C29.6035 2.00987 32.4928 1.44788 35.8118 1.71896C39.1308 1.99003 42.6746 3.21979 46.4498 5.39499C50.225 7.57681 53.7688 10.4462 57.0878 14.0033C60.4068 17.5603 63.2961 21.4611 65.749 25.6925C68.2019 29.9305 70.1457 34.3603 71.5804 39.0016C73.0085 43.6363 73.7291 48.119 73.7291 52.4429Z"
                      fill="#FFBABA"
                      stroke="#FF3333"
                      stroke-width="1.32231"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M42.377 34.3537L33.2266 38.929L41.134 34.4199C41.4844 34.2149 41.8944 34.1951 42.377 34.3537Z"
                      fill="#FFBABA"
                      stroke="#FF3333"
                      stroke-width="1.32231"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </CancelIcon>
                <Typography
                  size="lg"
                  weight="medium"
                  align="center"
                  color={Colors.grey400}
                >
                  We’re sorry to see you go
                </Typography>
              </CanceledModalBody>
            </ModalTemplate>
          )}
        </Modal>
      </>
    </DashboardLayout>
  );
};

export default SettingsSubscriptions;

const Buttons = styled.div`
  display: grid;
  grid-template-columns: 18rem 16rem;
  justify-content: space-between;
  align-items: center;
  margin-top: 2.4rem;
  @media only screen and (max-width: 769px) {
    grid-template-columns: 1fr;
    gap: 1.6rem;
  }
`;

const CanceledModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3.2rem;
  align-items: center;
`;

const CancelIcon = styled.div`
  width: 8rem;
  height: 8rem;
`;
